import { useQuery, useMutation } from "@tanstack/react-query";
import { apiFetch, ApiError } from "@/lib/api";

export interface DebatePricing {
  /** Final price in EUR (e.g. 1.99). */
  price: number;
  /** Always null since the intro offer is over. Kept for back-compat. */
  originalPrice: null;
  /** Always false since the intro offer is over. Kept for back-compat. */
  isIntro: false;
  currency: string;
}

interface RawPricing {
  price_cents?: number;
  price?: number;
  amount?: number;
  unit_amount?: number;
  currency?: string;
}

function toEuros(v: unknown): number | null {
  if (typeof v !== "number" || Number.isNaN(v)) return null;
  if (Number.isInteger(v) && v >= 100) return v / 100;
  return v;
}

export function useDebatePricing(enabled = true) {
  return useQuery({
    queryKey: ["pricing", "debate"],
    enabled,
    queryFn: async (): Promise<DebatePricing> => {
      const raw = await apiFetch<RawPricing>("/api/pricing/debate");
      const price =
        toEuros(raw.price_cents) ??
        toEuros(raw.price) ??
        toEuros(raw.amount) ??
        toEuros(raw.unit_amount) ??
        1.99;
      return {
        price,
        originalPrice: null,
        isIntro: false,
        currency: (raw.currency ?? "EUR").toUpperCase(),
      };
    },
    staleTime: 5 * 60_000,
  });
}

interface CheckoutResponse {
  url?: string;
  checkout_url?: string;
}

export function useDebateCheckout() {
  return useMutation({
    mutationFn: async () => {
      const res = await apiFetch<CheckoutResponse>(
        "/api/billing/debate/checkout",
        { method: "POST", body: JSON.stringify({}) },
      );
      const url = res.url ?? res.checkout_url;
      if (!url) throw new Error("URL de paiement manquante");
      window.location.href = url;
      return url;
    },
  });
}

/**
 * Checkout pour l'abonnement mensuel standard (29 €/mois, 30 débats inclus).
 * POST /api/subscription/checkout { kind: "plan", plan: "standard" }
 */
export function useSubscriptionCheckout() {
  return useMutation({
    mutationFn: async () => {
      const res = await apiFetch<CheckoutResponse>(
        "/api/subscription/checkout",
        {
          method: "POST",
          body: JSON.stringify({ kind: "plan", plan: "standard" }),
        },
      );
      const url = res.checkout_url ?? res.url;
      if (!url) throw new Error("URL de paiement manquante");
      window.location.href = url;
      return url;
    },
  });
}

export function subscriptionCheckoutErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 401) return "Veuillez vous connecter pour vous abonner.";
    if (err.status === 400) return "Requête invalide. Veuillez réessayer.";
    if (err.status === 503)
      return "Le paiement est momentanément indisponible. Réessayez dans un instant.";
    if (err.status >= 500)
      return "Impossible d'ouvrir le paiement. Réessayez dans un instant.";
  }
  return "Impossible d'ouvrir le paiement. Veuillez réessayer.";
}

export function formatEUR(v: number): string {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: Number.isInteger(v) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(v);
}
